/**
 * Analytics Tracking Utilities
 *
 * Centralized analytics tracking for Firebase Analytics and Sentry
 * Tracks key user actions and events across the platform
 */

import { analytics, queuedLogEvent } from './firebase';
import { captureSentryException, captureSentryMessage, addSentryBreadcrumb } from './sentry';

/**
 * Track page views
 */
export const trackPageView = (pageName) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb('page_view', 'navigation', {
    page_title: pageName,
    page_location: window.location.href,
    page_path: window.location.pathname
  });

  try {
    queuedLogEvent(analytics, 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track address searches
 */
export const trackAddressSearch = (chain, searchSource = 'unknown') => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb(
    `search`,
    'user',
    { chain, searchSource } // searchSource: 'url' or 'search_box'
  );

  try {
    queuedLogEvent(analytics, 'search', { chain, searchSource });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track wallet connections
 */
export const trackWalletConnect = (method, chain = 'unknown') => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb('wallet_connect', 'user', { 
    method, // 'metamask' or 'walletconnect'
    chain   // 'ethereum', 'bitcoin', etc. (matching other events)
  });

  try {
    queuedLogEvent(analytics, 'wallet_connect', { method, chain });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track wallet disconnections
 */
export const trackWalletDisconnect = () => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb('wallet_disconnect', 'user');

  try {
    queuedLogEvent(analytics, 'wallet_disconnect', {});
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track comment creation
 */
export const trackCommentCreate = (level, chain) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb('comment_create', 'user', { level, chain });

  try {
    queuedLogEvent(analytics, 'comment_create', {
      level, // 0 = top-level, 1 = reply
      chain
    });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track comment edits
 */
export const trackCommentEdit = (commentId) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb('comment_edit', 'user', { commentId });

  try {
    queuedLogEvent(analytics, 'comment_edit', { commentId });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track comment deletion
 */
export const trackCommentDelete = (commentId, level) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb('comment_delete', 'user', { commentId, level });

  try {
    queuedLogEvent(analytics, 'comment_delete', { commentId, level });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track votes
 */
export const trackVote = (voteType, chain) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb(`vote`, 'user', { voteType, chain });

  try {
    queuedLogEvent(analytics, 'vote', {
      voteType, // 'upvote' or 'downvote'
      chain
    });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track content flags/reports
 */
export const trackContentFlag = (reason, commentId) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb(`flag_comment`, 'user', { reason, commentId });

  try {
    queuedLogEvent(analytics, 'flag_comment', {
      reason,
      commentId
    });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track alias creation
 */
export const trackAliasCreate = (chain) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb(`create alias`, 'user', { chain });

  try {
    queuedLogEvent(analytics, 'alias_create', { chain });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track notification interactions
 */
export const trackNotificationClick = (context) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb('notification_click', 'user', context);

  try {
    queuedLogEvent(analytics, 'notification_click', context);
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

/**
 * Track errors
 * Sends errors to Sentry for detailed error tracking and debugging
 */
export const trackError = (errorType, errorMessage, context = {}) => {
  // Capture to Sentry for detailed error tracking with stack traces
  try {
    // If error is an Error object, use captureSentryException
    if (context.error instanceof Error) {
      captureSentryException(context.error, {
        errorType,
        errorMessage,
        ...context
      });
    } else {
      // Otherwise, capture as message with error level
      captureSentryMessage(`${errorType}: ${errorMessage}`, 'error', context);
    }
  } catch (error) {
    console.warn('Sentry error:', error);
  }
};

/**
 * Track feature usage
 */
export const trackFeatureUse = (featureName, details = {}) => {
  // Add breadcrumb for Sentry
  addSentryBreadcrumb(`Used feature: ${featureName}`, 'user', details);

  try {
    queuedLogEvent(analytics, 'feature_use', {
      feature_name: featureName,
      ...details
    });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

// Export all tracking functions
const analyticsUtils = {
  trackPageView,
  trackAddressSearch,
  trackWalletConnect,
  trackWalletDisconnect,
  trackCommentCreate,
  trackCommentEdit,
  trackCommentDelete,
  trackVote,
  trackContentFlag,
  trackAliasCreate,
  trackNotificationClick,
  trackError,
  trackFeatureUse
};

export default analyticsUtils;
